/**
 * Context summarization strategy values (mirrors backend enum ordering).
 */
export const ContextSummarizationStrategy = {
  LastXMinusY: 0,
  SlidingWindow: 1,
  ImportanceBased: 2,
  TimeBased: 3,
  Hybrid: 4,
} as const;

export type ContextSummarizationStrategy =
  (typeof ContextSummarizationStrategy)[keyof typeof ContextSummarizationStrategy];

/**
 * Summarizer type values (mirrors backend enum ordering).
 */
export const SummarizerType = {
  Standard: 0,
  Custom: 1,
} as const;

export type SummarizerType = (typeof SummarizerType)[keyof typeof SummarizerType];

/**
 * DTO for agent context window configuration.
 */
export interface AgentContextWindowConfigDto {
  id: string;
  agentDefinitionId: string;
  maxContextTokens: number;
  summarizationThreshold: number;
  summarizationStrategy: ContextSummarizationStrategy;
  summarizerType: SummarizerType;
  customSummarizerPrompt?: string | null;
  messagesToRetain: number;
  messagesToSummarize: number;
  enableAutoSummarization: boolean;
  compressionRatio: number;
  priorityMessagesJson?: string | null;
  tenantId: string;
  projectId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Request model for updating a context window configuration.
 */
export interface UpdateContextWindowConfigRequest {
  maxContextTokens: number;
  summarizationThreshold: number;
  summarizationStrategy: ContextSummarizationStrategy;
  summarizerType: SummarizerType;
  customSummarizerPrompt?: string | null;
  messagesToRetain: number;
  messagesToSummarize: number;
  enableAutoSummarization: boolean;
  compressionRatio: number;
  priorityMessagesJson?: string | null;
}

/**
 * Current context usage snapshot for a session.
 */
export interface ContextUsage {
  sessionId: string;
  modelId: string;
  modelName: string;
  maxContextTokens: number;
  summarizationThreshold: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  summaryTokens: number;
  utilization: number;
  shouldSummarize: boolean;
  capturedAtUtc: string;
}

/**
 * DTO for stored session context summaries.
 */
export interface AgentContextSummaryDto {
  id: string;
  sessionId: string;
  summaryText: string;
  originalTokenCount: number;
  compressedTokenCount: number;
  messagesSummarized: number;
  messageRangeStart: string;
  messageRangeEnd: string;
  strategy: ContextSummarizationStrategy;
  summarizerType: SummarizerType;
  createdAt: string;
}

